/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { ActionManager, ActionManagerFn } from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { defaultValue } from './ActionInvokeControllerFunction';

const ActionInvokeControllerFunctionManager: ActionManagerFn<
  'ActionInvokeControllerFunction'
> = (platform: Platform): ActionManager<'ActionInvokeControllerFunction'> => {
  return {
    ...baseActionManager(platform, 'ActionInvokeControllerFunction'),
    defaultValue,
    props: {
      ...baseActionProps,
      functionName: {
        label: 'Function Name',
        type: 'controllerFunction',
        alwaysShow: true,
        required: true,
        helpText: 'Choose a function name',
      },
    },
    name: 'Invoke Controller Function',
    getDisplayText: (item) => item.props.functionName,
    replaceableWith: ['ActionInvokeCloudFunction'],
    icon: { iconName: 'code' },
    description: `**Overview**<br/>
The Invoke Controller Function action is a client-side extensibility component designed to execute custom JavaScript or TypeScript logic defined within the Page Controller. It serves as a bridge between the platform's declarative action framework and custom imperative code, allowing developers to implement complex logic, algorithms, or UI manipulations that go beyond standard configuration capabilities.<br/><br/>
This component executes entirely within the user's browser context. It is designed to be a direct counterpart to the "Invoke Cloud Function" action, offering a similar workflow for triggering code but targeting local controller methods instead of server-side endpoints.

**Key Features**<br/>
* **Client-Side Execution:** Triggers custom functions immediately in the browser, providing a zero-latency option for logic that does not require server resources.
* **Component Interchangeability:** Explicitly configured in the replaceableWith array to be swappable with ActionInvokeCloudFunction, allowing developers to easily refactor logic from client-to-server or vice versa.
* **Code Integration:** The functionName property utilizes a specialized controllerFunction editor type, likely providing autocomplete or selection from the actual methods exported in the page's associated code file.

**Properties**<br/>
* **Function Name (Required):** A property of type controllerFunction.
  * *Function:* Selects the specific JavaScript/TypeScript method to be executed from the page controller.
  * *Visibility:* Marked as alwaysShow: true to ensure it is prominent in the configuration panel.

**Use Cases**<br/>
* **Complex Calculations:** Performing mathematical operations (e.g., loan amortization schedules) directly on the client side to update UI inputs instantly.
* **Data Transformation:** Reformatting a dataset using JavaScript array methods (map, filter, reduce) before passing it to a Grid or Chart component.
* **Custom UI Logic:** Interacting with third-party libraries or browser APIs that are initialized within the page controller.`,
  };
};

export default ActionInvokeControllerFunctionManager;
